import React from 'react';
import { AlertCircle, Check } from 'lucide-react';

const DeviceModeModal = ({
  showDeviceModeModal,
  pendingDeviceMode,
  deviceMode,
  deviceDimensions,
  selectedScalingOption,
  setSelectedScalingOption,
  setShowDeviceModeModal,
  setPendingDeviceMode,
  applyDeviceModeChange
}) => {
  if (!showDeviceModeModal || !pendingDeviceMode) return null;

  return (
    <div className="fixed inset-0 bg-gray-950/40 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="max-w-lg w-full overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        <div className="flex items-start gap-2.5 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" />
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Change Canvas Size?</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Switching from <span className="font-medium text-gray-900 dark:text-gray-100">{deviceDimensions[deviceMode].name}</span> to <span className="font-medium text-gray-900 dark:text-gray-100">{deviceDimensions[pendingDeviceMode].name}</span> will change the canvas dimensions.
            </p>
            <p className="mt-1 text-sm text-gray-500 tabular-nums dark:text-gray-400">
              Current: {deviceDimensions[deviceMode].width} × {deviceDimensions[deviceMode].height}px → 
              New: {deviceDimensions[pendingDeviceMode].width} × {deviceDimensions[pendingDeviceMode].height}px
            </p>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">How should elements be adjusted?</p>

          <div className="space-y-2">
            <button
              onClick={() => setSelectedScalingOption('proportional')}
              className={`w-full text-left p-3.5 rounded-xl border transition-colors ${
                selectedScalingOption === 'proportional'
                  ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                  : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
              }`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 ${
                  selectedScalingOption === 'proportional'
                    ? 'border-primary-600 bg-primary-600'
                    : 'border-gray-300 bg-white dark:border-white/20 dark:bg-gray-900'
                }`}>
                  {selectedScalingOption === 'proportional' && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Scale Proportionally (Recommended)</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    All elements will resize and reposition proportionally to maintain the same relative layout.
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSelectedScalingOption('center')}
              className={`w-full text-left p-3.5 rounded-xl border transition-colors ${
                selectedScalingOption === 'center'
                  ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                  : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
              }`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 ${
                  selectedScalingOption === 'center'
                    ? 'border-primary-600 bg-primary-600'
                    : 'border-gray-300 bg-white dark:border-white/20 dark:bg-gray-900'
                }`}>
                  {selectedScalingOption === 'center' && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Keep Sizes, Adjust Positions</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Elements keep their current sizes but positions adjust to stay centered.
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSelectedScalingOption('none')}
              className={`w-full text-left p-3.5 rounded-xl border transition-colors ${
                selectedScalingOption === 'none'
                  ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                  : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
              }`}
            >
              <div className="flex items-start">
                <div className={`flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 ${
                  selectedScalingOption === 'none'
                    ? 'border-primary-600 bg-primary-600'
                    : 'border-gray-300 bg-white dark:border-white/20 dark:bg-gray-900'
                }`}>
                  {selectedScalingOption === 'none' && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Keep Everything As-Is</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Elements maintain exact positions and sizes (some may go off-screen).
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <button
            onClick={() => {
              setShowDeviceModeModal(false);
              setPendingDeviceMode(null);
            }}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={() => applyDeviceModeChange(pendingDeviceMode, selectedScalingOption)}
            className="btn btn-primary"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceModeModal;
